import { useState } from "react";
import type { FileResponse } from "@patent-studio/api-types";
import { refusalDetail } from "@/services/api-client";
import { RetryAction } from "@/components/shared/RetryAction";

function replaceFailure(error: unknown, name: string): string {
  return refusalDetail(error, `Could not replace ${name}.`);
}

/**
 * What a document the worker could not read offers: read it again where the
 * server will take it, or supply a better copy. The row reports its own
 * outcome, since a component can hold several such rows.
 */
export function UnreadableActions({
  file,
  taskId,
  accept,
  onReplace,
}: {
  file: FileResponse;
  taskId: string;
  accept: Record<string, string[]>;
  onReplace: (file: FileResponse, replacement: File) => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [replacing, setReplacing] = useState(false);
  const inputId = `replace-${file.file_id}`;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {file.can_retry && (
        <RetryAction
          taskId={taskId}
          fileId={file.file_id}
          filename={file.original_filename}
          whenRefused={`Could not read ${file.original_filename} again.`}
          onError={setError}
        />
      )}
      <label
        htmlFor={inputId}
        className="inline-flex h-7 cursor-pointer items-center rounded-md border border-border bg-card px-2.5 text-[11.5px] font-semibold hover:bg-muted"
      >
        {replacing ? "Replacing…" : "Replace"}
      </label>
      <input
        id={inputId}
        type="file"
        aria-label={`Replace ${file.original_filename}`}
        accept={Object.values(accept).flat().join(",")}
        disabled={replacing}
        className="sr-only"
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          // Cleared at once: a browser fires no change for the same file twice.
          event.target.value = "";
          if (!chosen) return;
          setError(null);
          setReplacing(true);
          onReplace(file, chosen)
            .catch((err: unknown) =>
              setError(replaceFailure(err, file.original_filename)),
            )
            .finally(() => setReplacing(false));
        }}
      />
      {error && (
        <p role="alert" className="basis-full text-[11px] text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
